// Centro Médico → Catálogos → Misiones (/medical/misiones).
//
// Una misión ES un partner marcado como centro de costo: es lo que la ficha del
// paciente pide en «Centro de costo (misión)» y lo que sale en los impresos.
// Crearla desde aquí la deja marcada sola — antes había que ir a Administración
// → Partner y acordarse del tic, y si se olvidaba la misión no aparecía en la
// ficha sin que nadie entendiera por qué. RPCs del script 852.
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Archive, ArchiveRestore, Pencil, Plus, Search, Target, Users } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { getErrorMessage } from '@/lib/api/client';
import { useDialogoEnLinea } from '@/components/ui/PanelIntegrado';
import { AutoTextarea, Campo, Modal, fieldCls, medRpc } from './shared';
import { SelectPais } from '@/components/ui/SelectPais';
import { SortTh, useSort } from '@/features/control-bancario/shared';
import { ExportarBotones } from '@/lib/utils/exportarTabla';
import type { Celda } from '@/lib/utils/exportarTabla';

interface Mision {
  id: string; codigo: string | null; nombre: string;
  ciudad: string | null; provincia: string | null; pais: string | null;
  direccion: string | null; telefono: string | null; email: string | null; notas: string | null;
  activa: boolean; created_at: string; pacientes: number;
}

const FORM0 = {
  codigo: '', nombre: '', ciudad: '', provincia: '', pais: 'Ecuador',
  direccion: '', telefono: '', email: '', notas: '', activa: true,
};

export function Misiones() {
  const qc = useQueryClient();
  const dlg = useDialogoEnLinea();
  const [verArchivadas, setVerArchivadas] = useState(false);
  const [busqueda, setBusqueda] = useState('');
  const [form, setForm] = useState<typeof FORM0 | null>(null);
  const [editId, setEditId] = useState<string | null>(null);
  const set = (k: keyof typeof FORM0, v: string | boolean) => setForm((f) => (f ? { ...f, [k]: v } : f));

  const { data: misiones = [], isFetching } = useQuery<Mision[]>({
    queryKey: ['med-misiones', verArchivadas],
    queryFn: () => medRpc<Mision[]>('med_misiones', { _incluir_archivadas: verArchivadas }),
    staleTime: 0, refetchOnMount: 'always',
  });

  const invalidar = () => {
    void qc.invalidateQueries({ queryKey: ['med-misiones'] });
    // La ficha del paciente lee el mismo catálogo: si no se invalida, la misión
    // recién creada no aparece en su desplegable hasta recargar.
    void qc.invalidateQueries({ queryKey: ['med-centros-costo'] });
  };

  const guardar = useMutation({
    mutationFn: () => medRpc<{ id: string; codigo: string }>('med_mision_guardar', {
      _data: form, _id: editId,
    }),
    onSuccess: (r) => {
      invalidar(); setForm(null); setEditId(null);
      toast.success(editId ? 'Misión actualizada' : `Misión ${r.codigo} creada como centro de costo`);
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const archivar = useMutation({
    mutationFn: (v: { id: string; archivar: boolean }) =>
      medRpc<{ pacientes: number }>('med_mision_archivar', { _id: v.id, _archivar: v.archivar }),
    onSuccess: (r, v) => {
      invalidar();
      toast.success(v.archivar
        ? `Misión archivada${r.pacientes ? ` · ${r.pacientes} paciente(s) la conservan en su historial` : ''}`
        : 'Misión reactivada');
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const filtradas = useMemo(() => {
    const q = busqueda.trim().toLowerCase();
    if (!q) return misiones;
    return misiones.filter((m) =>
      `${m.codigo ?? ''} ${m.nombre} ${m.ciudad ?? ''}`.toLowerCase().includes(q));
  }, [misiones, busqueda]);

  const accessor = (m: Mision, col: string) => {
    switch (col) {
      case 'codigo': return m.codigo;
      case 'nombre': return m.nombre;
      case 'ciudad': return m.ciudad;
      case 'pacientes': return Number(m.pacientes);
      case 'estado': return m.activa ? 1 : 0;
      default: return null;
    }
  };
  const sort = useSort(filtradas, accessor, 'codigo');

  const abrirNueva = () => { setEditId(null); setForm({ ...FORM0 }); };
  const abrirEditar = (m: Mision) => {
    setEditId(m.id);
    setForm({
      codigo: m.codigo ?? '', nombre: m.nombre, ciudad: m.ciudad ?? '', provincia: m.provincia ?? '',
      pais: m.pais ?? 'Ecuador', direccion: m.direccion ?? '', telefono: m.telefono ?? '',
      email: m.email ?? '', notas: m.notas ?? '', activa: m.activa,
    });
  };

  const filas: Celda[][] = sort.sorted.map((m) => [
    m.codigo, m.nombre, m.ciudad, m.pacientes, m.activa ? 'Activa' : 'Archivada',
  ]);

  return (
    <div className="p-6">
      {dlg.ui}
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div>
          <h1 className="flex items-center gap-2 text-xl font-semibold">
            <Target className="h-5 w-5 text-primary" /> Misiones
          </h1>
          <p className="text-sm text-muted-foreground">
            Se crean como centro de costo y quedan disponibles en la ficha del paciente
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="pointer-events-none absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <input className={`${fieldCls} w-56 pl-8`} placeholder="Buscar misión"
                   value={busqueda} onChange={(e) => setBusqueda(e.target.value)} />
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={verArchivadas} onChange={(e) => setVerArchivadas(e.target.checked)} />
            Ver archivadas
          </label>
          <Button onClick={abrirNueva}><Plus className="mr-1 h-4 w-4" /> Nueva misión</Button>
        </div>
      </div>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0">
          <CardTitle className="text-base">
            {sort.sorted.length} misión(es){verArchivadas ? ' · incluye archivadas' : ''}
          </CardTitle>
          <ExportarBotones
            nombre="misiones" titulo="Misiones"
            headers={['Código', 'Misión', 'Ciudad', 'Pacientes', 'Estado']}
            colsDerecha={[3]}
            filas={filas}
          />
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-muted-foreground">
                  <th className="w-10 py-2 pr-3 text-right">N°</th>
                  <SortTh col="codigo" sort={sort}>Código</SortTh>
                  <SortTh col="nombre" sort={sort}>Misión</SortTh>
                  <SortTh col="ciudad" sort={sort}>Ciudad</SortTh>
                  <SortTh col="pacientes" sort={sort} align="right">Pacientes</SortTh>
                  <SortTh col="estado" sort={sort} align="center">Estado</SortTh>
                  <th className="py-2 pr-3" />
                </tr>
              </thead>
              <tbody>
                {sort.sorted.map((m, i) => (
                  <tr key={m.id} className="border-b last:border-0">
                    <td className="py-2 pr-3 text-right text-muted-foreground">{i + 1}</td>
                    <td className="py-2 pr-3 font-mono">{m.codigo}</td>
                    <td className="py-2 pr-3">
                      <div className="font-medium">{m.nombre}</div>
                      {m.notas && <div className="text-xs text-muted-foreground">{m.notas}</div>}
                    </td>
                    <td className="py-2 pr-3">{m.ciudad ?? '—'}</td>
                    <td className="py-2 pr-3 text-right">
                      {Number(m.pacientes) > 0
                        ? <span className="inline-flex items-center gap-1"><Users className="h-3.5 w-3.5 text-muted-foreground" />{m.pacientes}</span>
                        : <span className="text-muted-foreground">0</span>}
                    </td>
                    <td className="py-2 pr-3 text-center">
                      <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${m.activa
                        ? 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300'
                        : 'bg-muted text-muted-foreground'}`}>
                        {m.activa ? 'Activa' : 'Archivada'}
                      </span>
                    </td>
                    <td className="whitespace-nowrap py-2 pr-3 text-right">
                      <Button size="sm" variant="ghost" title="Editar" onClick={() => abrirEditar(m)}>
                        <Pencil className="h-4 w-4" />
                      </Button>
                      {m.activa ? (
                        <Button size="sm" variant="ghost" title="Archivar"
                                onClick={async () => {
                                  const detalle = Number(m.pacientes) > 0
                                    ? `"${m.nombre}" tiene ${m.pacientes} paciente(s) asignado(s). Al archivarla deja de ofrecerse para nuevos pacientes, pero se conserva en su historial.`
                                    : undefined;
                                  if (!(await dlg.confirmar(`¿Archivar "${m.nombre}"?`, { detalle, peligro: true }))) return;
                                  archivar.mutate({ id: m.id, archivar: true });
                                }}>
                          <Archive className="h-4 w-4" />
                        </Button>
                      ) : (
                        <Button size="sm" variant="ghost" title="Reactivar"
                                onClick={() => archivar.mutate({ id: m.id, archivar: false })}>
                          <ArchiveRestore className="h-4 w-4" />
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
                {sort.sorted.length > 0 && (
                  <tr className="border-t-2 bg-muted/30 font-semibold">
                    <td className="py-2 pr-3" colSpan={4}>Totales ({sort.sorted.length})</td>
                    <td className="py-2 pr-3 text-right">
                      {sort.sorted.reduce((a, m) => a + Number(m.pacientes), 0)}
                    </td>
                    <td colSpan={2} />
                  </tr>
                )}
                {sort.sorted.length === 0 && !isFetching && (
                  <tr><td colSpan={7} className="py-6 text-center text-muted-foreground">
                    Sin misiones. Crea la primera con «Nueva misión» — nacerá marcada como centro
                    de costo y aparecerá en la ficha del paciente.
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {form && (
        <Modal title={editId ? 'Editar misión' : 'Nueva misión'} onClose={() => { setForm(null); setEditId(null); }} wide>
          <div className="grid grid-cols-2 gap-3">
            <Campo label="Código">
              <input className={fieldCls} value={form.codigo}
                     onChange={(e) => set('codigo', e.target.value)}
                     placeholder={editId ? '' : 'Automático (M001, M002…)'} />
            </Campo>
            <Campo label="Nombre de la misión *">
              <input className={fieldCls} value={form.nombre} onChange={(e) => set('nombre', e.target.value)} />
            </Campo>
            <Campo label="Ciudad">
              <input className={fieldCls} value={form.ciudad} onChange={(e) => set('ciudad', e.target.value)} />
            </Campo>
            <Campo label="Provincia / región">
              <input className={fieldCls} value={form.provincia} onChange={(e) => set('provincia', e.target.value)} />
            </Campo>
            <Campo label="País">
              <SelectPais className={fieldCls} value={form.pais} onChange={(v) => set('pais', v)} />
            </Campo>
            <Campo label="Teléfono">
              <input className={fieldCls} value={form.telefono} onChange={(e) => set('telefono', e.target.value)} />
            </Campo>
            <Campo label="Dirección">
              <input className={fieldCls} value={form.direccion} onChange={(e) => set('direccion', e.target.value)} />
            </Campo>
            <Campo label="Email">
              <input className={fieldCls} value={form.email} onChange={(e) => set('email', e.target.value)} />
            </Campo>
            <div className="col-span-2">
              <Campo label="Notas">
                <AutoTextarea value={form.notas} onChange={(e) => set('notas', e.target.value)} minRows={2} />
              </Campo>
            </div>
            <label className="col-span-2 flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.activa} onChange={(e) => set('activa', e.target.checked)} />
              Activa (se ofrece al asignar la misión de un paciente)
            </label>
          </div>

          <p className="mt-3 rounded border border-dashed p-2 text-xs text-muted-foreground">
            La misión se guarda como un tercero marcado <b>centro de costo</b>, que es lo que la
            ficha del paciente y los impresos buscan. No hace falta crearla también en
            Administración → Partner.
          </p>

          <div className="mt-4 flex justify-end gap-2 border-t pt-3">
            <Button variant="outline" onClick={() => { setForm(null); setEditId(null); }}>Cancelar</Button>
            <Button isLoading={guardar.isPending} disabled={!form.nombre.trim()}
                    onClick={() => guardar.mutate()}>
              {editId ? 'Guardar' : 'Crear misión'}
            </Button>
          </div>
        </Modal>
      )}
    </div>
  );
}
